import { ChevronRight, Download, ExternalLink, Info } from 'lucide-react';
import { Link } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { AI_APP_LABELS } from '@/features/ai-apps/recipes';
import type {
  AiAppId,
  AiAppRecipe,
  ClaudePlan,
  RecipeLink,
  RecipeSection,
  RecipeStep,
} from '@/features/ai-apps/types';
import { CopyField } from '@/features/mcp-access/components/copy-field';
import { cn } from '@/lib/utils';
import { orgPath } from '@/utils/org-path';

import { AppLogo } from './app-picker';
import { Emphasis } from './emphasis';

const opensElsewhere = (href: string) => /^https?:\/\//.test(href);

const wrappingButton = 'h-auto min-h-8 max-w-full whitespace-normal text-left';

const StepLink = ({
  link,
  variant,
}: {
  link: RecipeLink;
  variant: 'default' | 'link';
}) => {
  if (link.internal) {
    return (
      <Button asChild variant={variant} size='sm' className={wrappingButton}>
        <Link to={orgPath(link.href)}>{link.label}</Link>
      </Button>
    );
  }
  const newTab = opensElsewhere(link.href);
  return (
    <Button asChild variant={variant} size='sm' className={wrappingButton}>
      <a
        href={link.href}
        {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {link.label}
        {newTab && <ExternalLink className='size-3.5' aria-hidden />}
        {newTab && <span className='sr-only'> (opens in a new tab)</span>}
      </a>
    </Button>
  );
};

const StepView = ({
  step,
  number,
  onCopied,
}: {
  step: RecipeStep;
  number: number;
  onCopied: (label: string) => void;
}) => (
  <li className='flex gap-3' data-testid={`step-${step.id}`}>
    <span
      aria-hidden
      className='flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold'
    >
      {number}
    </span>
    <div className='flex min-w-0 flex-1 flex-col gap-2'>
      <p className='text-sm leading-6 text-muted-foreground'>
        <Emphasis text={step.text} />
      </p>
      {step.link || (step.downloads?.length ?? 0) > 0 ? (
        <div className='flex flex-wrap items-center gap-2'>
          {step.downloads?.map((file) => (
            <Button
              key={file.id}
              asChild
              variant='outline'
              size='sm'
              title={file.description}
              className={wrappingButton}
            >
              <a
                href={file.url}
                download={file.fileName}
                data-testid={`download-${file.id}`}
              >
                <Download className='size-3.5' aria-hidden />
                {`Download ${file.label}`}
              </a>
            </Button>
          ))}
          {step.link && <StepLink link={step.link} variant='default' />}
          {step.fallbackLink && (
            <StepLink link={step.fallbackLink} variant='link' />
          )}
        </div>
      ) : null}
      {step.snippet && <CopyField snippet={step.snippet} onCopied={onCopied} />}
    </div>
  </li>
);

const SectionBody = ({
  section,
  onCopied,
}: {
  section: RecipeSection;
  onCopied: (label: string) => void;
}) => (
  <>
    {section.note && (
      <p className='text-sm text-muted-foreground'>
        <Emphasis text={section.note} />
      </p>
    )}
    {section.steps.length > 0 && (
      <ol className='flex flex-col gap-4'>
        {section.steps.map((step, index) => (
          <StepView
            key={step.id}
            step={step}
            number={index + 1}
            onCopied={onCopied}
          />
        ))}
      </ol>
    )}
  </>
);

/* Optional sections stay out of the way: a one-line note when they have no
 * steps, otherwise a closed disclosure the person opens if it applies. */
const SectionView = ({
  section,
  onCopied,
}: {
  section: RecipeSection;
  onCopied: (label: string) => void;
}) => {
  if (section.tone === 'optional' && section.steps.length === 0) {
    return (
      <p
        className='text-sm text-muted-foreground'
        data-testid={`section-${section.id}`}
      >
        <span className='font-medium text-foreground'>{section.title}: </span>
        <Emphasis text={section.note ?? ''} />
      </p>
    );
  }
  if (section.tone === 'optional') {
    return (
      <details
        className='group rounded-lg border px-4 py-3'
        data-testid={`section-${section.id}`}
      >
        <summary className='flex cursor-pointer list-none items-center gap-2 text-sm font-semibold [&::-webkit-details-marker]:hidden'>
          <ChevronRight
            className='size-4 shrink-0 transition-transform group-open:rotate-90'
            aria-hidden
          />
          {section.title}
        </summary>
        <div className='mt-3 flex flex-col gap-3'>
          <SectionBody section={section} onCopied={onCopied} />
        </div>
      </details>
    );
  }
  return (
    <section
      aria-labelledby={`ai-apps-section-${section.id}`}
      data-testid={`section-${section.id}`}
      className={cn(
        'flex flex-col gap-3',
        section.tone === 'admin' &&
          'rounded-lg border border-primary/30 bg-primary/5 p-4',
      )}
    >
      <h3
        id={`ai-apps-section-${section.id}`}
        className='flex flex-wrap items-center gap-2 text-sm font-semibold'
      >
        {section.title}
        {section.tone === 'admin' && <Badge variant='secondary'>Admins</Badge>}
      </h3>
      <SectionBody section={section} onCopied={onCopied} />
    </section>
  );
};

const PLAN_OPTIONS: { value: ClaudePlan; label: string }[] = [
  { value: 'team', label: 'Team or Enterprise' },
  { value: 'personal', label: 'Free, Pro or Max' },
];

const PlanToggle = ({
  value,
  onChange,
}: {
  value: ClaudePlan;
  onChange: (plan: ClaudePlan) => void;
}) => (
  <div className='flex flex-wrap items-center gap-2 text-sm'>
    <span className='text-muted-foreground'>How do you use Claude?</span>
    <ToggleGroup
      type='single'
      value={value}
      onValueChange={(next) => next && onChange(next as ClaudePlan)}
      aria-label='Your Claude plan'
      className='rounded-lg border p-0.5'
    >
      {PLAN_OPTIONS.map((option) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          data-testid={`claude-plan-${option.value}`}
          className='h-auto px-3 py-1 font-normal text-muted-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground'
        >
          {option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  </div>
);

export const RecipeView = ({
  recipe,
  serverName,
  claudePlan,
  onClaudePlanChange,
  onPickApp,
  onCopied,
}: {
  recipe: AiAppRecipe;
  serverName: string;
  claudePlan: ClaudePlan;
  onClaudePlanChange: (plan: ClaudePlan) => void;
  onPickApp: (app: AiAppId) => void;
  onCopied: (label: string) => void;
}) => (
  <section
    aria-labelledby='ai-apps-recipe-title'
    data-testid='ai-apps-recipe'
    data-app={recipe.app}
    className='flex flex-col gap-6 rounded-xl border bg-card p-5 shadow-xs sm:p-6'
  >
    <div className='flex items-center gap-3'>
      <AppLogo app={recipe.app} />
      <h2 id='ai-apps-recipe-title' className='text-lg font-semibold'>
        {`Add ${serverName} to ${AI_APP_LABELS[recipe.app]}`}
      </h2>
    </div>

    {recipe.app === 'claude' && recipe.available && (
      <PlanToggle value={claudePlan} onChange={onClaudePlanChange} />
    )}

    {recipe.requirement && (
      <p className='flex gap-2 text-sm text-muted-foreground'>
        <Info className='mt-0.5 size-4 shrink-0' aria-hidden />
        {recipe.requirement}
      </p>
    )}

    {!recipe.available && (
      <div
        className='flex flex-col gap-3 rounded-lg border border-dashed p-4 text-sm'
        data-testid='ai-apps-unavailable'
      >
        <p>{recipe.unavailableReason}</p>
        {recipe.tryInstead && recipe.tryInstead.length > 0 && (
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-muted-foreground'>Works instead:</span>
            {recipe.tryInstead.map((app) => (
              <Button
                key={app}
                variant='outline'
                size='sm'
                onClick={() => onPickApp(app)}
              >
                {AI_APP_LABELS[app]}
              </Button>
            ))}
          </div>
        )}
      </div>
    )}

    {recipe.sections.map((section) => (
      <SectionView key={section.id} section={section} onCopied={onCopied} />
    ))}

    {recipe.verify && (
      <div
        className='flex flex-col gap-2 rounded-lg bg-muted/50 p-4'
        data-testid='ai-apps-verify'
      >
        <p className='text-sm font-semibold'>Check it works</p>
        <CopyField
          snippet={{
            id: 'verify_prompt',
            label: 'Ask this in a new chat',
            text: recipe.verify.prompt,
            language: 'text',
          }}
          onCopied={onCopied}
        />
        <p className='text-sm text-muted-foreground'>{recipe.verify.expect}</p>
      </div>
    )}
  </section>
);
